import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { UserProfile } from '@/types/user';
import type { Feature, SubscriptionPlan } from '@/config/plans';
import { 
  hasFeatureAccess, 
  isSubscriptionActive,
  getMinimumPlanForFeature,
  getInactiveSubscriptionMessage,
  PLAN_LABELS
} from '@/config/plans';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const isSupabaseConfigured = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

interface AccessControlResult {
  hasAccess: boolean;
  reason?: string;
  minimumPlan?: SubscriptionPlan;
  upgradeRequired?: boolean;
  subscriptionInactive?: boolean;
}

/**
 * Hook para controle de acesso baseado em planos
 * 
 * @example
 * ```tsx
 * const { userProfile, checkFeatureAccess, isLoading } = useAccessControl();
 * 
 * const reportAccess = checkFeatureAccess('reports');
 * if (!reportAccess.hasAccess) {
 *   // Mostrar mensagem de upgrade
 * }
 * ```
 */
export function useAccessControl() {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carrega o perfil do usuário com informações de assinatura
   */
  const loadUserProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Modo demo/offline
      if (!isSupabaseConfigured) {
        const demoAuth = localStorage.getItem('demoAuth') === 'true';
        if (demoAuth) {
          setUserProfile({
            id: 'demo-profile',
            user_id: 'demo-user',
            name: 'Usuário Demo',
            avatar: '🔥',
            subscription_plan: 'enterprise', // Demo tem acesso total
            subscription_status: 'active',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
        setIsLoading(false);
        return;
      }

      // Verifica autenticação
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      if (!session?.user) {
        setUserProfile(null);
        setIsLoading(false);
        return;
      }

      // Busca perfil do usuário
      const { data, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', session.user.id)
        .single();

      if (profileError) throw profileError;

      const profileData = data as UserProfile;
      setUserProfile(profileData);

      // Sincronização automática em segundo plano do brinde VidSell (se houver código vinculado)
      if (profileData.vidsell_claim_code) {
        import('@/services/vidsellService').then(({ syncVidSellSubscription }) => {
          syncVidSellSubscription(profileData).then((updated) => {
            if (updated) {
              supabase
                .from('profiles')
                .select('*')
                .eq('user_id', session.user.id)
                .single()
                .then(({ data: fresh }) => {
                  if (fresh) setUserProfile(fresh as UserProfile);
                });
            }
          }).catch(console.error);
        });
      }
      console.error('Erro ao carregar perfil do usuário:', err);
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
      
      // Em caso de erro, assume plano gratuito para não bloquear acesso
      setUserProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Verifica se o usuário tem acesso a uma funcionalidade
   */
  const checkFeatureAccess = useCallback((feature: Feature): AccessControlResult => {
    // Se não há perfil carregado, nega acesso
    if (!userProfile) {
      return {
        hasAccess: false,
        reason: 'Perfil de usuário não encontrado',
        upgradeRequired: false,
      };
    }

    // Verifica se a assinatura está ativa (incluindo checagem de data de expiração)
    const subscriptionIsActive = isSubscriptionActive(
      userProfile.subscription_status, 
      userProfile.subscription_ends_at
    );
    
    if (!subscriptionIsActive) {
      const isExpired = Boolean(
        userProfile.subscription_ends_at && 
        new Date(userProfile.subscription_ends_at).getTime() < Date.now()
      );
      return {
        hasAccess: false,
        reason: isExpired 
          ? 'Seu período de assinatura/brinde expirou. Renove seu plano para continuar acessando.' 
          : getInactiveSubscriptionMessage(userProfile.subscription_status),
        subscriptionInactive: true,
        upgradeRequired: false,
      };
    }

    // Verifica se o plano tem acesso à funcionalidade
    const hasAccess = hasFeatureAccess(userProfile.subscription_plan, feature);

    if (!hasAccess) {
      const minimumPlan = getMinimumPlanForFeature(feature);
      return {
        hasAccess: false,
        reason: `Esta funcionalidade está disponível no plano ${PLAN_LABELS[minimumPlan]} ou superior`,
        minimumPlan,
        upgradeRequired: true,
      };
    }

    return {
      hasAccess: true,
    };
  }, [userProfile]);

  /**
   * Verifica se o usuário pode acessar múltiplas funcionalidades
   */
  const checkMultipleFeatures = useCallback((features: Feature[]): Record<Feature, AccessControlResult> => {
    const results: Record<string, AccessControlResult> = {};
    
    for (const feature of features) {
      results[feature] = checkFeatureAccess(feature);
    }
    
    return results;
  }, [checkFeatureAccess]);

  /**
   * Retorna informações sobre o plano atual do usuário
   */
  const getCurrentPlan = useCallback(() => {
    if (!userProfile) return null;
    
    return {
      plan: userProfile.subscription_plan,
      status: userProfile.subscription_status,
      label: PLAN_LABELS[userProfile.subscription_plan],
      isActive: isSubscriptionActive(userProfile.subscription_status),
      startedAt: userProfile.subscription_started_at,
      endsAt: userProfile.subscription_ends_at,
      trialEndsAt: userProfile.trial_ends_at,
    };
  }, [userProfile]);

  // Carrega perfil ao montar o componente
  useEffect(() => {
    loadUserProfile();
  }, [loadUserProfile]);

  // Recarrega perfil quando houver mudança de autenticação
  useEffect(() => {
    if (!isSupabaseConfigured) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        loadUserProfile();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [loadUserProfile]);

  return {
    userProfile,
    isLoading,
    error,
    checkFeatureAccess,
    checkMultipleFeatures,
    getCurrentPlan,
    refreshProfile: loadUserProfile,
  };
}
